import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { PartialId } from '@attain-apps/string-builder'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as common from '../../../../common'

export const DOC = gql`
  query AgentConfiguration(
    $tenantId: ID!
    $zoneId: ID!
    $agentId: String!
    $conversationId: ID
  ) {
    tenant(id: $tenantId) {
      zone(id: $zoneId) {
        agentConfiguration(agentId: $agentId, conversationId: $conversationId) {
          agentId
          conversationId
          context {
            dmsResources
            instructions
            knowledgeBaseFiles {
              knowledgeBaseId
              s3Uri
            }
          }
        }
      }
    }
  }
`

const TRANSFORMER = graphql.transformers.OUT.select<
  graphql.schema.AgentConfigurationQuery,
  ents.core.agent.Configuration | undefined,
  'tenant'
>(
  'tenant',
  graphql.transformers.OUT.select(
    'zone',
    graphql.transformers.OUT.select(
      'agentConfiguration',
      graphql.transformers.OUT.object({
        agentId: graphql.transformers.OUT.nonNull(
          common.gql.enums.agentId.TRANSFORMER,
        ),
        context: {
          usingFullInput: graphql.transformers.OUT.default(
            graphql.transformers.OUT.object({
              dmsResources: {
                usingFullInput: graphql.transformers.OUT.select(
                  'context',
                  graphql.transformers.OUT.select(
                    'dmsResources',
                    // NOTE: Cast should be safe — json() parses the AWSJSON
                    // string and returns the value as-is (unknown). Since
                    // dmsResources is nullable in the schema, json() will
                    // return undefined when the field is absent, which matches
                    // the optional entity type.
                    graphql.transformers.OUT.json() as transformers.Transformer<
                      'entity',
                      string | null | undefined,
                      ents.core.agent.Configuration.Context['dmsResources']
                    >,
                  ),
                ),
              },
              instructions: {
                usingFullInput: graphql.transformers.OUT.select(
                  'context',
                  graphql.transformers.OUT.select(
                    'instructions',
                    transformers.PASS_THROUGH,
                  ),
                ),
              },
              knowledgeBaseFiles: {
                usingFullInput: graphql.transformers.OUT.select(
                  'context',
                  graphql.transformers.OUT.select(
                    'knowledgeBaseFiles',
                    graphql.transformers.OUT.list(
                      graphql.transformers.OUT.object({
                        knowledgeBaseId: graphql.transformers.OUT.nonNull(
                          transformers.PASS_THROUGH,
                        ),
                        s3Uri: graphql.transformers.OUT.nonNull(
                          transformers.PASS_THROUGH,
                        ),
                      }),
                      { onNullish: 'remove' },
                    ),
                  ),
                ),
              },
            }),
            // This is database default
            {},
          ),
        },
        conversationId: {
          usingFullInput: graphql.transformers.OUT.select(
            'conversationId',
            graphql.transformers.OUT.validate(
              transformers.PASS_THROUGH,
              TypeAsserter.Factory.from(
                TypeChecker.Factory.optional(PartialId.isPartialId),
              ),
            ),
          ),
        },
      }),
    ),
  ),
)

export const executor = graphql.createExecutor<
  graphql.schema.AgentConfigurationQuery,
  ents.core.agent.Configuration | undefined,
  graphql.schema.AgentConfigurationQueryVariables
>({
  query: DOC,
  transformer: TRANSFORMER,
})
